'use client';

import { use } from 'react';

import styled from '@emotion/styled';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { SurveyThemeContext } from '@/shared-utilities';
import { type GetProjectQuery } from '@/survey-graphql';

import SurveyAiFormsAiSectionMobileHeader from './survey-ai-forms-ai-section-mobile-header';
import SurveyAiFormsProgressDots from './survey-ai-forms-progress-dots';
import SurveyAiFormsSubmitButton from './survey-ai-forms-submit-button';
import { useSwipeGesture } from './survey-ai-forms-swipe.hooks';
import { type ViewMode } from './survey-ai-forms.types';

const ThemedDiv = styled.div<{ themeStyles: Record<string, string> }>`
  ${(props) => props.themeStyles}
`;

interface Question {
  id: string;
}

interface Props {
  onSubmit: () => void;
  totalQuestions: number;
  questions?: Question[];
  onQuestionClick?: (questionId: string) => void;
  onViewModeToggle?: () => void;
  viewMode?: ViewMode;
  progress: number;
  estimatedTotalTimeInSeconds: number;
  project: GetProjectQuery['project'];
  isSubmitting?: boolean;
  canSubmit?: boolean;
  isPreviewMode?: boolean;
}

export default function SurveyAiFormsAiSectionMobileCompletion({
  onSubmit,
  totalQuestions,
  questions,
  onQuestionClick,
  onViewModeToggle,
  viewMode = 'ai',
  progress,
  estimatedTotalTimeInSeconds,
  project,
  isSubmitting = false,
  canSubmit = false,
  isPreviewMode = false,
}: Props) {
  const t = useTranslations('Common');
  const tAiForms = useTranslations('AIForms');
  const tCompletion = useTranslations('Completion');

  const { input: themeInput, styles } = use(SurveyThemeContext);

  const themeSettings = themeInput?.themeSettings ?? project.themeSettings;
  const hasBackgroundImage = !!themeSettings?.bgImage;

  // Swipe gesture for drag handle - swipe up to toggle to Form mode
  const swipeHandlers = useSwipeGesture({
    onSwipeUp: onViewModeToggle,
    threshold: 30, // 30px threshold for drag handle
  });

  return (
    <div
      data-test='mobile-completion'
      data-is-preview-mode={isPreviewMode}
      className='flex h-screen w-full flex-col px-2 pt-2 data-[is-preview-mode=true]:relative'
    >
      {/* Custom corner cutout */}
      <ThemedDiv
        data-has-background-image={hasBackgroundImage}
        data-is-preview-mode={isPreviewMode}
        className='absolute right-2 bottom-8 size-8 bg-(image:--brand-bg-image) bg-auto bg-bottom-right bg-no-repeat data-[has-background-image=false]:bg-(--brand-bg-color) data-[is-preview-mode=true]:bottom-9'
        themeStyles={styles}
      />

      {/* Main AI Section with Background */}
      <ThemedDiv
        data-has-background-image={hasBackgroundImage}
        className='relative flex flex-1 flex-col rounded-4xl rounded-br-none bg-(--brand-bg-color) bg-(image:--brand-bg-image) bg-cover bg-center bg-no-repeat data-[has-background-image=false]:bg-(--brand-bg-color)'
        themeStyles={styles}
      >
        {/* Mobile Header - Logo and Progress Ring */}
        <SurveyAiFormsAiSectionMobileHeader
          progress={progress}
          estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
          viewMode={viewMode}
          onViewModeToggle={onViewModeToggle}
        />

        {/* Content - Completion Message - Centered with proper spacing */}
        <div className='mx-auto flex w-full max-w-180 flex-1 flex-col items-center justify-center px-2'>
          <h2 className='text-center [font-family:var(--brand-font)] text-2xl/9 font-semibold'>
            <span className='text-(--brand-secondary)'>
              {tCompletion('thankYou')}{' '}
            </span>
            {/* oxlint-disable jsx-a11y/prefer-tag-over-role */}
            <span role='img' aria-label={t('smilingFaceAria')}>
              😊
            </span>
            {/* oxlint-enable jsx-a11y/prefer-tag-over-role */}
          </h2>
        </div>

        {/* Footer with Progress Dots and Submit Button */}
        <div className='flex items-end justify-between px-2 pb-4'>
          {/* Left spacer */}
          <div className='w-30' />

          {/* Progress Dots - Using reusable component with mobile styling */}
          <SurveyAiFormsProgressDots
            currentIndex={totalQuestions - 1}
            total={totalQuestions}
            questions={questions}
            onQuestionClick={onQuestionClick}
          />

          {/* Submit Button - Using reusable component with mobile variant */}
          <div className='flex w-30 justify-end'>
            <SurveyAiFormsSubmitButton
              onClick={onSubmit}
              isMobile={true}
              loading={isSubmitting}
              disabled={!canSubmit}
            />
          </div>
        </div>
      </ThemedDiv>

      {/* Turn off AI Section - Only show when AI is enabled */}
      {viewMode === 'ai' && onViewModeToggle && (
        <div className='z-10 h-16 rounded-4xl bg-white px-4 py-2'>
          <button
            onClick={onViewModeToggle}
            data-test='turn-off-ai-button'
            className='flex w-full flex-col items-center justify-center gap-2'
          >
            {/* Drag handle indicator - swipe up to toggle to Form mode */}
            <div
              className='h-1 w-12 cursor-grab rounded-lg bg-gray-300 active:cursor-grabbing'
              onTouchStart={swipeHandlers.onTouchStart}
              onTouchMove={swipeHandlers.onTouchMove}
              onTouchEnd={swipeHandlers.onTouchEnd}
              onPointerDown={swipeHandlers.onPointerDown}
              onPointerMove={swipeHandlers.onPointerMove}
              onPointerUp={swipeHandlers.onPointerUp}
            />

            <div className='flex items-center gap-2'>
              <FontAwesomeIcon
                icon={faSparkles}
                className='size-4 text-black'
              />
              <span className='[font-family:var(--brand-font)] text-base font-semibold text-black'>
                {tAiForms('modeToggle.turnOffAi')}
              </span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
